import { useState, useEffect, useMemo } from 'react';
import type { FC } from 'react';

import classNames from 'classnames';

import { VideoPreview } from '../../VideoPreview/VideoPreview';
import { Image } from '../../Image/Image';
import { getImageSources } from '../../../helpers/images';
import type { ImageSources } from '../../../helpers/images';

import * as styles from './SplashImage.module.scss';

interface SplashImageProps {
  previewUrlProp?: string;
  onPreviewEnded?: () => void;
  lazy?: boolean;
  imageUrl?: string;
  iconImageUrl?: string;
  verticalImageSources?: ImageSources;
  variant?: string;
  className?: string;
}

const PREVIEW_DELAY_IN_MILLISECONDS = 1000;

const SplashImage: FC<SplashImageProps> = ({
  previewUrlProp,
  onPreviewEnded,
  lazy,
  imageUrl,
  verticalImageSources,
  variant,
  className,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    setPreviewUrl(undefined);

    if (!previewUrlProp) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setPreviewUrl(previewUrlProp);
    }, PREVIEW_DELAY_IN_MILLISECONDS);

    return () => window.clearTimeout(timeout);
  }, [previewUrlProp]);

  const imageSources = useMemo(
    () =>
      getImageSources({
        imageUrl,
        imagePreset: 'splashBackground',
      }),
    [imageUrl]
  );

  return (
    <>
      {previewUrl && (
        <VideoPreview
          url={previewUrl}
          className={classNames(styles.videoPreview, className)}
          onEnded={onPreviewEnded}
        />
      )}
      <Image
        className={className}
        imageSources={
          verticalImageSources && variant === 'carousel'
            ? verticalImageSources
            : imageSources
        }
        lazy={lazy}
        isRounded={false}
      />
    </>
  );
};

export { SplashImage };
